import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  getFirestore,
} from "firebase/firestore";
import * as XLSX from "xlsx";

const StocktakeComparison = () => {
  const db = getFirestore();

  const [rows, setRows] = useState([]);       // raw scans (duplicates preserved)
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setStatus("Loading scans…");

      // Pull product codes once
      const cylInfoSnap = await getDocs(collection(db, "cyl_info"));
      const productCodeMap = {};
      cylInfoSnap.forEach((d) => {
        const data = d.data();
        if (data?.productCode) productCodeMap[d.id] = data.productCode;
      });

      // Pull all scans
      const scansSnap = await getDocs(collection(db, "stocktake_scanned"));
      const list = scansSnap.docs.map((d) => {
        const data = d.data() || {};
        const ts =
          (data.ScannedTime && data.ScannedTime.toDate && data.ScannedTime.toDate()) ||
          (data.ScannedTime?.seconds ? new Date(data.ScannedTime.seconds * 1000) : null);

        const ProductId = data.ProductId || "";
        const ProductCode = productCodeMap[ProductId] || ProductId || "UNKNOWN";
        const Special = (data.Special ?? "").toString();
        const Type =
          Special === "50" ? "STOCK" :
          Special === "49" ? "ORDER" :
          Special || "UNKNOWN";

        return {
          id: d.id,
          SerialNumber: data.SerialNumber || "",
          Special,
          Type,
          ProductId,
          ProductCode,
          LocationId: data.LocationId || "",
          ScannedTime: ts,
          ScannedTimeISO: ts ? ts.toISOString() : "",
        };
      });

      // Sort newest first
      list.sort((a, b) => {
        const ta = a.ScannedTime ? a.ScannedTime.getTime() : 0;
        const tb = b.ScannedTime ? b.ScannedTime.getTime() : 0;
        return tb - ta;
      });

      setRows(list);
      setStatus(`Loaded ${list.length} scans.`);
      setLoading(false);
    })().catch((e) => {
      console.error(e);
      setStatus("Failed to load scans.");
      setLoading(false);
    });
  }, [db]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.SerialNumber || "").toLowerCase().includes(q) ||
      (r.ProductId || "").toLowerCase().includes(q) ||
      (r.ProductCode || "").toLowerCase().includes(q) ||
      (r.LocationId || "").toLowerCase().includes(q) ||
      (r.Special || "").toLowerCase().includes(q) ||
      (r.Type || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  // Group for the second sheet: Serial + ProductId + Special + LocationId
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const key = `${r.SerialNumber}::${r.ProductId}::${r.Special}::${r.LocationId}`;
      const cur = map.get(key);
      if (cur) {
        cur.Count += 1;
      } else {
        map.set(key, {
          SerialNumber: r.SerialNumber,
          ProductId: r.ProductId,
          ProductCode: r.ProductCode,
          Special: r.Special,
          Type: r.Type,
          LocationId: r.LocationId,
          Count: 1,
        });
      }
    }
    return Array.from(map.values());
  }, [filtered]);

  const autoFitCols = (jsonRows) => {
    // crude auto-width based on content length
    const keys = jsonRows.length ? Object.keys(jsonRows[0]) : [];
    return keys.map((k) => {
      const maxLen = Math.max(
        k.length,
        ...jsonRows.map((r) => (r[k] == null ? 0 : String(r[k]).length))
      );
      return { wch: Math.min(Math.max(10, maxLen + 2), 40) }; // clamp 10..40
    });
  };

  const exportExcel = () => {
    // Sheet 1: Raw Scans (preserve duplicates, one row per scan)
    const rawSheetRows = filtered.map((r) => ({
      SerialNumber: r.SerialNumber,
      Special: r.Special,
      Type: r.Type,
      ProductId: r.ProductId,
      ProductCode: r.ProductCode,
      LocationId: r.LocationId,
      ScannedTime: r.ScannedTime ? r.ScannedTime.toLocaleString() : "",
      ScannedTimeISO: r.ScannedTimeISO,
    }));
    const ws1 = XLSX.utils.json_to_sheet(rawSheetRows);
    ws1["!cols"] = autoFitCols(rawSheetRows);

    // Sheet 2: Grouped counts (Serial+ProductId+Special+LocationId)
    const groupedSheetRows = grouped.map((g) => ({
      SerialNumber: g.SerialNumber,
      ProductId: g.ProductId,
      ProductCode: g.ProductCode,
      Special: g.Special,
      Type: g.Type,
      LocationId: g.LocationId,
      Count: g.Count,
    }));
    const ws2 = XLSX.utils.json_to_sheet(groupedSheetRows);
    ws2["!cols"] = autoFitCols(groupedSheetRows);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws1, "Raw Scans");
    XLSX.utils.book_append_sheet(wb, ws2, "Grouped (S+P+Spec+Loc)");

    XLSX.writeFile(wb, "Stocktake_Scanned.xlsx");
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">📄 Stocktake — Scanned Items (Raw + Grouped)</h1>

      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search Serial / ProductId / ProductCode / Location / Special / Type…"
          className="w-full md:w-1/2 p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={exportExcel}
          disabled={loading || filtered.length === 0}
          className={`px-4 py-2 rounded-md text-white ${loading || filtered.length === 0 ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"}`}
        >
          Export Excel
        </button>
        <span className="text-sm text-gray-600">{status}</span>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto border rounded">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left">Serial</th>
                <th className="px-3 py-2 text-left">Special</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">ProductId</th>
                <th className="px-3 py-2 text-left">ProductCode</th>
                <th className="px-3 py-2 text-left">Location</th>
                <th className="px-3 py-2 text-left">Scanned</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                    No scans.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">{r.SerialNumber}</td>
                    <td className="px-3 py-2">{r.Special}</td>
                    <td className="px-3 py-2">{r.Type}</td>
                    <td className="px-3 py-2">{r.ProductId}</td>
                    <td className="px-3 py-2">{r.ProductCode}</td>
                    <td className="px-3 py-2">{r.LocationId}</td>
                    <td className="px-3 py-2">
                      {r.ScannedTime ? r.ScannedTime.toLocaleString() : ""}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 text-sm text-gray-600">
        <p>• **Raw Scans** sheet preserves every scan (duplicates included).<br/>
           • **Grouped** sheet counts identical (Serial + ProductId + Special + Location) combos.</p>
      </div>
    </div>
  );
};

export default StocktakeComparison;
